import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Minus,
  Package,
  Plus,
  Star,
  X,
} from 'react-feather';
import { useNavigate } from 'react-router-dom';

import ProductCard from '@/shared/components/UI/ProductCard';
import Button from '@/shared/components/UI/Button';
import LinkButton from '@/shared/components/UI/LinkButton';
import {
  getTotalPrice,
  formatNumber,
  formatPrice,
} from '@/shared/utils/priceUtils';
import { getCurrencySymbol } from '@/shared/utils/currencyUtils';
import { formatDateYYYYMMDD } from '@/shared/utils/dateUtils';

import { useProductDetail } from '../hooks/useProductDetail';
import { useScroll } from '../hooks/useScroll';
import { SafeHtmlRendererProps } from '../types/ProductPageTypes';
import ContinueShoppingModal from './ContinueShoppingModal';

const SafeHtmlRenderer = ({ htmlContent }: SafeHtmlRendererProps) => {
  const cleanHtml = DOMPurify.sanitize(htmlContent || '', {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'ul', 'ol', 'li', 'img'],
    ALLOWED_ATTR: ['src', 'alt', 'width', 'height'], // 이미지 속성 허용
  });

  return <>{parse(cleanHtml)}</>;
};

const ProductDetail = () => {
  const navigate = useNavigate();
  const {
    // State
    selectedOptions,
    optionsDropdown,
    options2Dropdown,
    selectedOption1,
    isDetailsOpen,
    isCartModalOpen,
    productData,
    mainImage,
    youMayLikeList,

    // State setters
    setIsDetailsOpen,
    setIsCartModalOpen,
    setMainImage,
    // Business logic functions
    hasSecondOption,
    handleFirstOptionSelect,
    handleSecondOptionSelect,
    removeOptionFromSelected,
    handleOptionQuantityChange,
    handleInputChange,
    getInputValue,
    getUniqueOptionValues1,
    getFilteredOptionsForSecond,
    calculateTotal,
    getDefaultPrice,
    addToCart,
    isSingleSimpleOption,
  } = useProductDetail();

  const { scrollContainerRef, handleScroll } = useScroll();

  const isSingleOption = isSingleSimpleOption();

  return (
    <div className="mx-auto my-8 mb-[100px] w-full max-w-[54rem]">
      <div className="relative flex justify-between border-b border-[#ddd]">
        {/* Left Section */}
        <div className="w-full max-w-[27.375rem]">
          {/* 대표 이미지 */}
          <img
            src={mainImage || ''}
            alt="product"
            className="h-[438px] w-[438px] rounded-2xl"
          />

          {/* 썸네일 리스트 */}
          <div className="mb-2 flex w-[438px] justify-between gap-3 border-b border-[#ddd] py-4">
            <button
              type="button"
              className="flex w-6 cursor-pointer items-center bg-transparent"
              onClick={() => handleScroll('left')}
            >
              <ChevronLeft
                className="h-6 w-6 text-[#999999]"
                strokeWidth={1.5}
              />
            </button>
            <div
              ref={scrollContainerRef}
              className="flex w-[380px] justify-start gap-2 overflow-x-scroll"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {(productData.product?.thumbnailUrlArray
                ? (() => {
                    try {
                      return JSON.parse(productData.product.thumbnailUrlArray);
                    } catch {
                      return [];
                    }
                  })()
                : []
              ).map((url: string, idx: number) => (
                <button
                  type="button"
                  key={url}
                  onClick={() => setMainImage(url)}
                >
                  <img
                    key={url}
                    src={url}
                    alt={`thumbnail-${idx}`}
                    className={`h-[68px] w-[68px] cursor-pointer rounded-2xl ${mainImage === url ? 'ring-1 ring-[#95699f]' : ''}`}
                  />
                </button>
              ))}
            </div>
            <button
              type="button"
              className="flex w-6 cursor-pointer items-center bg-transparent"
              onClick={() => handleScroll('right')}
            >
              <ChevronRight
                className="h-6 w-6 text-[#999999]"
                strokeWidth={1.5}
              />
            </button>
          </div>

          <div className="border-b border-[#ddd] py-6">
            <div className="mb-2 text-sm font-normal text-[#444]">
              <SafeHtmlRenderer
                htmlContent={productData.product?.description}
              />
            </div>
          </div>

          <details className="cursor-pointer" open={isDetailsOpen}>
            <summary
              className="relative flex h-[60px] list-none items-center text-base font-semibold leading-6 text-[#222]"
              onClick={(e) => {
                e.preventDefault();
                setIsDetailsOpen(!isDetailsOpen);
              }}
            >
              MUST READ AND AGREE BEFORE PURCHASE
              <ChevronDown
                className={`absolute right-0 h-6 w-6 transition-transform duration-200 ${isDetailsOpen ? 'rotate-180' : ''}`}
              />
            </summary>
            <div className="mb-6">
              <p className="mb-2 text-xs font-normal leading-[140%] text-[#666]">
                The above image is intended to help understanding and may differ
                from the actual one depending on the monitor resolution and
                measurement method.
              </p>
              <p className="mb-2 text-xs font-normal leading-[140%] text-[#666]">
                Size and composition can be changed depending on the production
                situation.
              </p>
              <p className="mb-2 text-xs font-normal leading-[140%] text-[#666]">
                Paper products cannot be exchanged or refunded because of
                unavoidable small damage, dents, or tears that occurred during
                delivery.
              </p>
              <p className="mb-2 text-xs font-normal leading-[140%] text-[#666]">
                Outbox is an anti-shock agent to protect the product and may
                cause damage (crushed, torn, stamped, etc.), discoloration, and
                contamination during delivery. This may not be exchanged or
                refunded.
              </p>
            </div>
          </details>
        </div>

        {/* Right Section */}
        <div className="sticky top-[0px] h-full w-full max-w-96 pb-4">
          <div>
            <h2 className="mb-3 mt-5 text-2xl font-semibold leading-[140%] text-[#444]">
              {productData.product?.title}
            </h2>
            <p className="mb-7 text-base font-normal text-[#666]">
              {productData.product?.vendor}
            </p>
            <div className="flex items-center justify-between border-b border-[#ddd] pb-5 text-[#95699f]">
              <div className="flex gap-2">
                {productData.product?.releaseDate && (
                  <>
                    <p className="text-base font-normal">Release :</p>
                    <p className="text-base font-normal">
                      {formatDateYYYYMMDD(productData.product.releaseDate)}
                    </p>
                  </>
                )}
              </div>
              <span className="block text-right text-2xl font-semibold text-[#95699f]">
                {getDefaultPrice() > 0
                  ? formatPrice(getDefaultPrice(), productData.currencyCode)
                  : 'Members Only'}{' '}
                {productData.currencyCode
                  ? getCurrencySymbol(productData.currencyCode)
                  : ''}
              </span>
            </div>
          </div>

          <div>
            {!isSingleOption && (
              <>
                <h4 className="mb-4 mt-5 text-base font-semibold text-[#222]">
                  OPTION
                </h4>
                <p className="mb-3 text-base font-normal text-[#222]">
                  {productData.optionList?.[0]?.optionNameKey1}
                </p>

                {/* First Option Dropdown */}
                <div className="relative h-9 w-full max-w-96 rounded-full border-[1.5px] border-[#ccc]">
                  <button
                    type="button"
                    className="mb-5 flex h-[34px] w-full cursor-pointer items-center justify-between border-0 bg-transparent px-4 pl-5 text-sm text-[#b995be] outline-0"
                    onClick={optionsDropdown.toggle}
                  >
                    {selectedOption1 || 'Select option 1'}
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${optionsDropdown.isOpen ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {optionsDropdown.isOpen && (
                    <ul className="absolute left-0 top-10 z-10 max-h-52 w-full overflow-y-scroll rounded-lg border-[1.5px] border-[#ccc] bg-white p-2 transition-all duration-300 ease-in">
                      {getUniqueOptionValues1().map((optionValue1) => {
                        // optionNameKey2가 없는 경우에만 MOQ 표시
                        const shouldShowMOQ = !hasSecondOption();
                        const firstOption = shouldShowMOQ
                          ? productData.optionList?.find(
                              (option) =>
                                option.optionNameValue1 === optionValue1,
                            )
                          : null;

                        return (
                          <button
                            key={optionValue1}
                            type="button"
                            className="flex w-full cursor-pointer items-center justify-between rounded-lg p-3 text-sm font-medium leading-[140%] text-[#666] hover:bg-gray-50"
                            onClick={() =>
                              handleFirstOptionSelect(optionValue1)
                            }
                          >
                            <p className="max-w-[14.5rem]">{optionValue1}</p>
                            {shouldShowMOQ && firstOption && (
                              <span className="ml-1 text-sm font-normal leading-[140%]">
                                {!firstOption.isSoldOut ? (
                                  <span className="text-xs text-[#666]">
                                    MOQ /
                                    <span className="ml-1 text-sm font-semibold">
                                      {formatNumber(
                                        firstOption.minimumOrderQuantity,
                                      )}
                                    </span>
                                  </span>
                                ) : (
                                  <span className="text-[#ee2626]">
                                    Sold Out
                                  </span>
                                )}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </>
            )}

            {/* Second Option Dropdown (only show if optionNameKey2 exists and is not empty and first option is selected) */}
            {hasSecondOption() && selectedOption1 && (
              <>
                <p className="mb-3 text-base font-normal text-[#222]">
                  {productData.optionList?.[0]?.optionNameKey2}
                </p>
                <div className="relative h-9 w-full max-w-96 rounded-full border-[1.5px] border-[#ccc]">
                  <button
                    type="button"
                    className="mb-5 flex h-[34px] w-full cursor-pointer items-center justify-between border-0 bg-transparent px-4 pl-5 text-sm text-[#b995be] outline-0"
                    onClick={options2Dropdown.toggle}
                  >
                    {selectedOptions.length > 0
                      ? `${selectedOptions.length} options selected`
                      : `Select ${productData.optionList?.[0]?.optionNameKey2 || 'option 2'}`}
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${options2Dropdown.isOpen ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {options2Dropdown.isOpen && (
                    <ul className="absolute left-0 top-10 z-10 max-h-52 w-full overflow-y-scroll rounded-lg border-[1.5px] border-[#ccc] bg-white p-2 transition-all duration-300 ease-in">
                      {getFilteredOptionsForSecond()?.map((option) => (
                        <button
                          key={option.uuid}
                          type="button"
                          className={`flex w-full items-center justify-between rounded-lg p-3 text-sm font-medium leading-[140%] ${
                            !option.isSoldOut
                              ? 'cursor-pointer text-[#666] hover:bg-gray-50'
                              : 'cursor-not-allowed bg-[#f4f4f4] text-[#bbb]'
                          }`}
                          onClick={() => handleSecondOptionSelect(option)}
                          disabled={option.isSoldOut}
                        >
                          <p className="max-w-[14.5rem]">
                            {option.optionNameValue2}
                          </p>
                          <span className="ml-1 text-sm font-normal leading-[140%]">
                            {!option.isSoldOut ? (
                              <span className="text-xs text-[#666]">
                                MOQ /
                                <span className="ml-1 text-sm font-semibold">
                                  {formatNumber(option.minimumOrderQuantity)}
                                </span>
                              </span>
                            ) : (
                              <span className="text-[#ee2626]">Sold Out</span>
                            )}
                          </span>
                        </button>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}

            {selectedOptions.map((selectedItem, index) => (
              <div
                key={selectedItem.option.uuid || index}
                className="my-5 rounded-lg border-[1.5px] border-[#cccccc] bg-white p-4"
              >
                <div className="mb-2 flex items-center justify-between">
                  <h4 className="text-xs font-normal leading-[140%] text-[#222]">
                    {productData.product?.title}
                  </h4>
                  {!isSingleOption && (
                    <X
                      className="h-5 w-5 cursor-pointer"
                      onClick={() => removeOptionFromSelected(index)}
                    />
                  )}
                </div>
                <p className="mb-4 text-xs font-normal text-[#bf9bc6]">
                  {selectedItem.option.optionNameValue1}
                  {selectedItem.option.optionNameValue2 &&
                    ` / ${selectedItem.option.optionNameValue2}`}
                </p>

                <div className="flex items-center justify-between">
                  <div className="flex h-9 w-[132px] justify-between rounded border">
                    <button
                      type="button"
                      className={`flex h-full w-[34px] items-center justify-center bg-transparent ${
                        selectedItem.quantity <=
                        (selectedItem.option.minimumOrderQuantity || 1)
                          ? 'cursor-not-allowed opacity-50'
                          : 'cursor-pointer'
                      }`}
                      onClick={() => handleOptionQuantityChange(index, -1)}
                      disabled={
                        selectedItem.quantity <=
                        (selectedItem.option.minimumOrderQuantity || 1)
                      }
                    >
                      <Minus className="h-4 w-4 text-[#444444]" />
                    </button>
                    <input
                      type="text"
                      className="max-w-14 border-0 px-2 text-center outline-0"
                      value={getInputValue(index, selectedItem.quantity)}
                      min={selectedItem.option.minimumOrderQuantity || 1}
                      max={selectedItem.option.stock}
                      onChange={(e) => handleInputChange(index, e.target.value)}
                    />
                    <button
                      type="button"
                      className={`flex h-full w-[34px] items-center justify-center bg-transparent ${
                        selectedItem.quantity >= selectedItem.option.stock
                          ? 'cursor-not-allowed opacity-50'
                          : 'cursor-pointer'
                      }`}
                      onClick={() => handleOptionQuantityChange(index, 1)}
                      disabled={
                        selectedItem.quantity >= selectedItem.option.stock
                      }
                    >
                      <Plus className="h-4 w-4 text-[#444444]" />
                    </button>
                  </div>
                  <span className="text-sm font-normal text-[#222]">
                    {selectedItem.option.productPrice?.totalPrice
                      ? formatPrice(
                          getTotalPrice(
                            selectedItem.option.productPrice.totalPrice,
                            selectedItem.quantity,
                            productData.currencyCode,
                          ),
                          productData.currencyCode,
                        )
                      : 'Members Only'}{' '}
                    {productData.currencySymbol || '원'}
                  </span>
                </div>
              </div>
            ))}
            <div className="mt-5 flex items-center justify-between border-t border-[#ddd] pt-3">
              <p className="text-base font-semibold">TOTAL</p>
              <span className="text-base font-semibold text-[#95699f]">
                {productData?.totalPrice !== null ? (
                  <>
                    {formatPrice(calculateTotal(), productData.currencyCode)}{' '}
                    {productData.currencyCode
                      ? getCurrencySymbol(productData.currencyCode)
                      : '원'}
                  </>
                ) : (
                  'Members Only'
                )}
              </span>
            </div>

            <div className="mt-9 flex justify-center">
              <Button
                type="button"
                variant="outline"
                size="md"
                onClick={addToCart}
                className="w-full max-w-[200px]"
              >
                Add to Cart
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Shopping Info Section */}
      <div className="mt-6 flex w-full max-w-[54rem] justify-between">
        <div className="w-full max-w-[420px]">
          <h2 className="mb-4 text-base font-semibold text-[#222]">
            Shipping Arrangement
          </h2>
          <img
            src="/images/shopping-arrangement.gif"
            alt="shipping"
            className="w-[420px]"
          />
          <div className="mt-4">
            <div className="flex items-center gap-3">
              <Star className="h-4 w-4" fill="currentColor" />
              <p className="text-xs font-normal leading-[140%] text-[#444]">
                Recording a one-take unboxing video can help verify any issues
                and ensure a smooth refund or support process.
              </p>
            </div>
          </div>
        </div>
        <div className="w-full max-w-[420px]">
          <h2 className="mb-4 text-base font-semibold text-[#222]">
            Return, Exchange & Refund
          </h2>
          <img src="/images/return.gif" alt="return" className="w-[420px]" />
          <div className="mt-4">
            <div className="flex items-center gap-3">
              <Package className="h-4 w-4" />
              <p className="text-xs font-normal leading-[140%] text-[#444]">
                Recording a one-take unboxing video can help verify any issues
                and ensure a smooth refund or support process.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Related Products Section */}
      <div className="mx-auto mb-[100px] mt-20 max-w-[54rem]">
        <div className="mb-9 flex items-center justify-between">
          <h4 className="text-xl font-semibold text-[#222]">
            You may also like
          </h4>
          <LinkButton
            to="/productlist/0?tag=pre&categoryName=PRE-ORDER"
            variant="text"
            rightIcon={<ChevronRight className="h-5 w-5" strokeWidth={1.5} />}
            className="gap-1 text-xs hover:text-[#666]"
          >
            VIEW ALL
          </LinkButton>
        </div>
        <div className="flex overflow-y-hidden overflow-x-scroll pb-3">
          {youMayLikeList.map((product) => (
            <div
              key={product.optionUuid}
              className="flex-shrink-0 cursor-pointer"
            >
              <ProductCard key={product.optionUuid} product={product} />
            </div>
          ))}
        </div>
      </div>

      <ContinueShoppingModal
        isOpen={isCartModalOpen}
        onClose={() => {
          setIsCartModalOpen(false);
        }}
        onContinueShopping={() => {
          setIsCartModalOpen(false);
          navigate('/');
        }}
      />
    </div>
  );
};

export default ProductDetail;
